import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  Close,
  CloseOutlined,
  History,
  SearchOutlined,
  StarOutlineRounded,
  StarRounded,
} from "@mui/icons-material";
import {
  Box,
  CircularProgress,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Tooltip,
  Typography,
} from "@mui/material";

import { blue, grey } from "@mui/material/colors";

import { isNil } from "lodash";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { usePipelineHook } from "../../../hooks/usePipelineHook";
import { useReduxHook } from "../../../hooks/useReduxHook";
import { useReduxLoanDrilldownHook } from "../../../hooks/useReduxLoanDrilldownHook";
import { loanPipelineSet } from "../../../redux/actions/loanPipelineActions";
import TextInput from "../../inputs/TextInput";
import Flx from "../../layout/Flx";
import Txt from "../../typography/Txt";
import BasicModal from "../BasicModal";
import OpenInNewTabButton from "../../ui/buttons/OpenInNewTabButton";

const RECENT_SEARCHES_KEY = "loanRecentSearches";
const FAVORITES_STORAGE_KEY = "lo-pipeline-favorite-loans";
const MAX_RECENT_SEARCHES = 5;
const MAX_RESULTS = 20;

const TABS = [
  { key: "search", label: "Recent Searchs" },
  { key: "my-loans", label: "My Loans" },
  { key: "favorites", label: "Favorites" },
];

const getFavoriteLoanIds = () => {
  try {
    return JSON.parse(localStorage.getItem(FAVORITES_STORAGE_KEY)) || [];
  } catch {
    return [];
  }
};

const filterLoansByUserEmail = (loans = [], userEmail) => {
  if (!userEmail) return [];
  return loans.filter(
    (l) =>
      l?.salesperson === userEmail ||
      l?.loanCloser === userEmail ||
      l?.processor === userEmail ||
      l?.underwriter === userEmail ||
      l?.permittedUsers?.includes(userEmail),
  );
};

const LoanSearchModal = memo(({ show, onClose, onSuccessFn }) => {
  const loanPipeline = useSelector((state) => state.loanPipeline);
  const [recentSearches, setRecentSearches] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [activeTab, setActiveTab] = useState("search");
  const [favoriteIds, setFavoriteIds] = useState(() => getFavoriteLoanIds());
  const { clearLoanDrilldownData } = useReduxLoanDrilldownHook();
  const { loading, getUserPipeline } = usePipelineHook();
  const { userEmail } = useReduxHook();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const listRef = useRef(null);

  const loanOptions = useMemo(() => {
    if (isNil(loanPipeline)) return [];
    return loanPipeline.map((loan) => ({
      label: `(${loan.loanNumber}) - ${loan.loanName}`,
      value: loan.loanNumber,
      loanName: loan.loanName,
      loanNumber: loan.loanNumber,
      loanStatus: loan.loanStatus,
      pipeline: loan.pipeline,
      sync_loan_id: loan.sync_loan_id,
      _id: loan._id,
    }));
  }, [loanPipeline]);

  const myLoans = useMemo(() => {
    if (isNil(loanPipeline)) return [];
    return filterLoansByUserEmail(loanPipeline, userEmail);
  }, [loanPipeline, userEmail]);

  const favoriteLoans = useMemo(() => {
    if (isNil(loanPipeline) || favoriteIds.length === 0) return [];
    return loanPipeline.filter((l) => favoriteIds.includes(l?._id));
  }, [loanPipeline, favoriteIds]);

  const filteredOptions = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return [];

    return loanOptions
      .filter((opt) => {
        return Object.values(opt).some((val) => {
          if (isNil(val)) return false;
          if (typeof val === "object") return false;
          return String(val).toLowerCase().includes(term);
        });
      })
      .slice(0, MAX_RESULTS);
  }, [loanOptions, searchTerm]);

  const showSearchResults =
    activeTab === "search" && searchTerm.trim().length > 0;

  // Reset highlight when results change / open modal
  useEffect(() => {
    if (!showSearchResults) {
      setHighlightedIndex(-1);
      return;
    }
    setHighlightedIndex(filteredOptions.length > 0 ? 0 : -1);
  }, [showSearchResults, filteredOptions.length]);

  // Load recent searches from localStorage
  useEffect(() => {
    const loadRecentSearches = () => {
      try {
        const saved = localStorage.getItem(RECENT_SEARCHES_KEY);
        if (saved) {
          setRecentSearches(JSON.parse(saved));
        }
      } catch (error) {
        console.error("Error loading recent searches:", error);
      }
    };

    if (show) {
      loadRecentSearches();
      setSearchTerm("");
      setHighlightedIndex(-1);
      setActiveTab("search");
      setFavoriteIds(getFavoriteLoanIds());
    }
  }, [show]);

  const saveRecentSearch = useCallback((loanNumber, loanName) => {
    try {
      const searchItem = {
        loanNumber,
        loanName,
        timestamp: Date.now(),
      };

      const saved = localStorage.getItem(RECENT_SEARCHES_KEY);
      let searches = saved ? JSON.parse(saved) : [];

      searches = searches.filter((item) => item.loanNumber !== loanNumber);
      searches.unshift(searchItem);
      searches = searches.slice(0, MAX_RECENT_SEARCHES);

      localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(searches));
      setRecentSearches(searches);
    } catch (error) {
      console.error("Error saving recent search:", error);
    }
  }, []);

  const deleteRecentSearch = useCallback((loanNumber, e) => {
    e.stopPropagation();
    try {
      const saved = localStorage.getItem(RECENT_SEARCHES_KEY);
      let searches = saved ? JSON.parse(saved) : [];

      searches = searches.filter((item) => item.loanNumber !== loanNumber);

      localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(searches));
      setRecentSearches(searches);
    } catch (error) {
      console.error("Error deleting recent search:", error);
    }
  }, []);

  const clearAllRecentSearches = useCallback(() => {
    try {
      localStorage.removeItem(RECENT_SEARCHES_KEY);
      setRecentSearches([]);
    } catch (error) {
      console.error("Error clearing recent searches:", error);
    }
  }, []);

  const handleOnClose = useCallback(() => {
    onClose(false);
  }, [onClose]);

  const handleOnClick = useCallback(
    (loanNumber, loanName) => {
      const loan = loanName
        ? { loanName }
        : loanOptions.find((opt) => opt.value === loanNumber);

      const finalLoanName = loanName || loan?.loanName || "Unknown";

      saveRecentSearch(loanNumber, finalLoanName);

      clearLoanDrilldownData();
      navigate(`/loan/${loanNumber}/summary`);
      handleOnClose();
    },
    [dispatch, navigate, handleOnClose, saveRecentSearch, loanOptions],
  );

  useEffect(() => {
    if (isNil(loanPipeline)) {
      getUserPipeline({
        onSuccessFn: (data) => {
          dispatch(loanPipelineSet(data));
        },
      });
    }
  }, []);

  const scrollItemIntoView = (index) => {
    const listEl = listRef.current;
    if (!listEl) return;
    const child = listEl.querySelector(`[data-result-index="${index}"]`);
    if (child?.scrollIntoView) {
      child.scrollIntoView({ block: "nearest" });
    }
  };

  const handleInputKeyDown = useCallback(
    (e) => {
      if (!showSearchResults) return;

      if (e.key === "ArrowDown") {
        e.preventDefault();
        setHighlightedIndex((prev) => {
          const next = Math.min(prev + 1, filteredOptions.length - 1);
          scrollItemIntoView(next);
          return next;
        });
      }

      if (e.key === "ArrowUp") {
        e.preventDefault();
        setHighlightedIndex((prev) => {
          const next = Math.max(prev - 1, 0);
          scrollItemIntoView(next);
          return next;
        });
      }

      if (e.key === "Enter") {
        if (highlightedIndex >= 0 && filteredOptions[highlightedIndex]) {
          e.preventDefault();
          const opt = filteredOptions[highlightedIndex];
          handleOnClick(opt.value, opt.loanName);
        }
      }

      if (e.key === "Escape") {
        e.preventDefault();
        handleOnClose();
      }
    },
    [
      showSearchResults,
      filteredOptions,
      highlightedIndex,
      handleOnClick,
      handleOnClose,
    ],
  );

  const handleTabChange = useCallback((tabKey) => {
    setActiveTab(tabKey);
    setHighlightedIndex(-1);
  }, []);

  return (
    <BasicModal
      show={show}
      onClose={handleOnClose}
      title="Loan Search"
      headerContent={
        <Box>
          <Flx sx={{ p: 2, pb: 1 }} flexGrow={1} ac>
            <SearchOutlined className="thin" />
            <TextInput
              autoFocus
              fullWidth
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e);
                if (e.trim().length > 0 && activeTab !== "search") {
                  setActiveTab("search");
                }
              }}
              onKeyDown={handleInputKeyDown}
              sx={{
                flexGrow: 1,
                fieldset: {
                  border: "none",
                },
              }}
              placeholder="Search for a loan by number or name"
              inputProps={{
                role: "combobox",
                "aria-expanded": showSearchResults,
                "aria-controls": "loan-search-results",
                "aria-activedescendant":
                  highlightedIndex >= 0
                    ? `loan-search-result-${highlightedIndex}`
                    : undefined,
              }}
            />
            <IconButton onClick={handleOnClose}>
              <CloseOutlined className="thin" />
            </IconButton>
          </Flx>
          {/* Tab bar */}
          <Flx
            ac
            sx={{
              px: 2,
              borderBottom: `1px solid ${grey[200]}`,
              gap: 0,
            }}
          >
            {TABS.map((tab) => {
              const isActive = activeTab === tab.key;
              const count =
                tab.key === "my-loans"
                  ? myLoans.length
                  : tab.key === "favorites"
                    ? favoriteLoans.length
                    : null;
              return (
                <Box
                  key={tab.key}
                  onClick={() => handleTabChange(tab.key)}
                  sx={{
                    px: 1.5,
                    py: 1,

                    fontWeight: isActive ? 600 : 400,
                    color: isActive ? "primary.main" : "text.secondary",
                    borderBottom: isActive
                      ? "2px solid"
                      : "2px solid transparent",
                    borderColor: isActive ? "primary.main" : "transparent",
                    cursor: "pointer",
                    userSelect: "none",
                    transition: "all 0.12s",
                    display: "flex",
                    alignItems: "center",
                    gap: "5px",
                    "&:hover": {
                      color: isActive ? "primary.main" : "text.primary",
                      bgcolor: isActive ? "transparent" : grey[50],
                    },
                  }}
                >
                  <Txt sx={{ fontSize: "12px" }}>{tab.label}</Txt>
                  {count != null && (
                    <Txt
                      sx={{
                        fontSize: "12px",
                        color: isActive ? "primary.main" : grey[400],
                        fontWeight: isActive ? 600 : 400,
                      }}
                    >
                      ({count})
                    </Txt>
                  )}
                </Box>
              );
            })}
          </Flx>
        </Box>
      }
      headerSx={{ pb: "0px !important" }}
      bodySx={{ p: 0 }}
      maxWidth="sm"
    >
      {loading || isNil(loanPipeline) ? (
        <Flx sx={{ py: 5 }} center>
          <CircularProgress />
        </Flx>
      ) : (
        <>
          {/* ── Search Tab ── */}
          {activeTab === "search" && (
            <>
              {/* Search Results Section (keyboard navigable) */}
              {showSearchResults && (
                <Box sx={{ p: 2 }}>
                  <Flx jb ac sx={{ mb: 1 }}>
                    <Txt sx={{ fontWeight: 500, fontSize: "11px" }}>
                      Search Results
                    </Txt>
                    <Txt variant="caption" sx={{ color: grey[500] }}>
                      {filteredOptions.length} result
                      {filteredOptions.length === 1 ? "" : "s"}
                    </Txt>
                  </Flx>

                  {filteredOptions.length === 0 ? (
                    <Box
                      sx={{
                        textAlign: "center",
                        py: 2,
                        color: grey[500],
                        borderTop: `1px solid ${grey[200]}`,
                      }}
                    >
                      <Typography variant="body2">
                        No loans match your search
                      </Typography>
                    </Box>
                  ) : (
                    <List
                      id="loan-search-results"
                      ref={listRef}
                      role="listbox"
                      sx={{
                        py: 0,
                        maxHeight: 480,
                        overflowY: "auto",
                        border: `1px solid ${grey[100]}`,
                        borderRadius: 2,
                      }}
                    >
                      {filteredOptions.map((opt, idx) => {
                        const isActive = idx === highlightedIndex;
                        return (
                          <ListItem
                            key={opt.value}
                            disablePadding
                            data-result-index={idx}
                            id={`loan-search-result-${idx}`}
                            role="option"
                            aria-selected={isActive}
                          >
                            <ListItemButton
                              selected={isActive}
                              onMouseEnter={() => setHighlightedIndex(idx)}
                              onClick={() =>
                                handleOnClick(opt.value, opt.loanName)
                              }
                              sx={{
                                py: 1,
                                px: 1,
                                borderRadius: 1,
                                borderBottom: `1px solid ${grey[100]}`,
                                "&.Mui-selected": {
                                  backgroundColor: grey[100],
                                },
                                "&.Mui-selected:hover": {
                                  backgroundColor: grey[100],
                                },
                                "&:hover": {
                                  backgroundColor: grey[50],
                                },
                              }}
                            >
                              <ListItemText
                                primary={
                                  <Flx ac gap={1}>
                                    <Txt
                                      sx={{
                                        px: 0.8,
                                        py: 0.4,
                                        fontSize: "10px",
                                        backgroundColor: grey[200],
                                        borderRadius: "40px",
                                        flexShrink: 0,
                                      }}
                                    >
                                      #{opt.loanNumber}
                                    </Txt>
                                    <Txt
                                      sx={{
                                        fontSize: "11px",
                                        flexShrink: 0,
                                      }}
                                    >
                                      {opt.loanName}
                                    </Txt>
                                  </Flx>
                                }
                                primaryTypographyProps={{
                                  variant: "body2",
                                  sx: { fontWeight: 400 },
                                }}
                              />
                            </ListItemButton>
                          </ListItem>
                        );
                      })}
                    </List>
                  )}
                </Box>
              )}

              {/* Recent Searches Section (only when not typing) */}
              {!showSearchResults && recentSearches.length > 0 && (
                <Box sx={{ p: 2 }}>
                  <Flx jb ac sx={{ mb: 1 }}>
                    <Flx ac gap={0.4}>
                      <History sx={{ fontSize: "15px", color: grey[600] }} />
                      <Txt sx={{ fontWeight: 500, fontSize: "11px" }}>
                        Recent Searches
                      </Txt>
                    </Flx>
                    <Txt
                      variant="caption"
                      sx={{
                        color: "primary.main",
                        cursor: "pointer",
                        "&:hover": { textDecoration: "underline" },
                      }}
                      onClick={clearAllRecentSearches}
                    >
                      Clear All
                    </Txt>
                  </Flx>

                  <List
                    sx={{
                      py: 0,
                      border: `1px solid ${grey[100]}`,
                      borderRadius: 2,
                    }}
                  >
                    {recentSearches.map((search) => (
                      <ListItem
                        key={search.loanNumber}
                        disablePadding
                        secondaryAction={
                          <Flx ac g={1}>
                            <Tooltip title="Open in new tab">
                              <Box
                                sx={{
                                  borderRadius: "50%",
                                  ":hover": {
                                    background: blue[50],
                                  },
                                }}
                              >
                                <OpenInNewTabButton
                                  size={15}
                                  to={`/loan/${search.loanNumber}`}
                                />
                              </Box>
                            </Tooltip>

                            <IconButton
                              edge="end"
                              size="small"
                              onClick={(e) =>
                                deleteRecentSearch(search.loanNumber, e)
                              }
                              sx={{
                                opacity: 0.6,
                                "&:hover": { opacity: 1 },
                              }}
                            >
                              <Close sx={{ fontSize: 15 }} />
                            </IconButton>
                          </Flx>
                        }
                      >
                        <ListItemButton
                          onClick={() =>
                            handleOnClick(search.loanNumber, search.loanName)
                          }
                          sx={{
                            py: 1,
                            px: 1,
                            borderRadius: 1,
                            borderBottom: `1px solid ${grey[100]}`,
                            "&:hover": {
                              backgroundColor: grey[50],
                            },
                          }}
                        >
                          <ListItemText
                            primary={
                              <Flx ac gap={1}>
                                <Txt
                                  sx={{
                                    px: 0.8,
                                    py: 0.4,
                                    fontSize: "10px",
                                    backgroundColor: grey[200],
                                    borderRadius: "40px",
                                    flexShrink: 0,
                                  }}
                                >
                                  #{search.loanNumber}
                                </Txt>
                                <Txt
                                  sx={{
                                    fontSize: "11px",
                                    flexShrink: 0,
                                  }}
                                >
                                  {search.loanName}
                                </Txt>
                              </Flx>
                            }
                            primaryTypographyProps={{
                              variant: "body2",
                              sx: { fontWeight: 400 },
                            }}
                          />
                        </ListItemButton>
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}

              {/* Show message if no recent searches and not typing */}
              {!showSearchResults && recentSearches.length === 0 && (
                <Box
                  sx={{
                    textAlign: "center",
                    py: 5,
                    color: grey[500],
                    borderTop: `1px solid ${grey[200]}`,
                  }}
                >
                  <History sx={{ fontSize: 24, mb: 1, opacity: 0.5 }} />
                  <Typography variant="body2">No recent searches</Typography>
                </Box>
              )}
            </>
          )}

          {/* ── Favorites Tab ── */}
          {activeTab === "favorites" && (
            <Box sx={{ p: 2 }}>
              <Flx jb ac sx={{ mb: 1 }}>
                <Txt sx={{ fontWeight: 500, fontSize: "11px" }}>
                  Favorited Loans
                </Txt>

                <Txt variant="caption" sx={{ color: grey[500] }}>
                  {favoriteLoans.length} loan
                  {favoriteLoans.length === 1 ? "" : "s"}
                </Txt>
              </Flx>

              {favoriteLoans.length === 0 ? (
                <Box
                  sx={{
                    textAlign: "center",
                    py: 4,
                    color: grey[500],
                    borderTop: `1px solid ${grey[200]}`,
                  }}
                >
                  <StarOutlineRounded
                    sx={{ fontSize: 24, mb: 1, opacity: 0.5 }}
                  />
                  <Typography variant="body2">No favorite loans yet</Typography>
                </Box>
              ) : (
                <List
                  sx={{
                    py: 0,
                    maxHeight: 480,
                    overflowY: "auto",
                    border: `1px solid ${grey[100]}`,
                    borderRadius: 2,
                  }}
                >
                  {favoriteLoans.map((loan) => (
                    <ListItem key={loan._id} disablePadding>
                      <ListItemButton
                        onClick={() =>
                          handleOnClick(loan.loanNumber, loan.loanName)
                        }
                        sx={{
                          py: 1,
                          px: 1,
                          borderRadius: 1,
                          borderBottom: `1px solid ${grey[100]}`,
                          "&:hover": { backgroundColor: grey[50] },
                        }}
                      >
                        <ListItemText
                          primary={
                            <Flx fw jb ac gap={1}>
                              <Flx ac gap={1}>
                                <StarRounded
                                  sx={{ fontSize: 14, color: "#f59e0b" }}
                                />
                                <Txt
                                  sx={{
                                    px: 0.8,
                                    py: 0.4,
                                    fontSize: "10px",
                                    backgroundColor: grey[200],
                                    borderRadius: "40px",
                                    flexShrink: 0,
                                  }}
                                >
                                  #{loan.loanNumber}
                                </Txt>
                                <Txt sx={{ fontSize: "11px", flexShrink: 0 }}>
                                  {loan.loanName}
                                </Txt>
                              </Flx>
                              <Flx ac gap={1}>
                                {loan.loanStatus && (
                                  <Txt
                                    sx={{
                                      fontSize: "10px",
                                      color: grey[500],
                                      ml: "auto",
                                      flexShrink: 0,
                                    }}
                                  >
                                    {loan.loanStatus}
                                  </Txt>
                                )}
                                <Tooltip title="Open in new tab">
                                  <Box
                                    sx={{
                                      borderRadius: "50%",
                                      ":hover": {
                                        background: blue[50],
                                      },
                                    }}
                                  >
                                    <OpenInNewTabButton
                                      size={15}
                                      to={`/loan/${loan.loanNumber}`}
                                    />
                                  </Box>
                                </Tooltip>
                              </Flx>
                            </Flx>
                          }
                          primaryTypographyProps={{
                            variant: "body2",
                            sx: { fontWeight: 400 },
                          }}
                        />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              )}
            </Box>
          )}

          {/* ── My Loans Tab ── */}
          {activeTab === "my-loans" && (
            <Box sx={{ p: 2 }}>
              <Flx jb ac sx={{ mb: 1 }}>
                <Txt sx={{ fontWeight: 500, fontSize: "11px" }}>My Loans</Txt>
                <Txt variant="caption" sx={{ color: grey[500] }}>
                  {myLoans.length} loan
                  {myLoans.length === 1 ? "" : "s"}
                </Txt>
              </Flx>

              {myLoans.length === 0 ? (
                <Box
                  sx={{
                    textAlign: "center",
                    py: 4,
                    color: grey[500],
                    borderTop: `1px solid ${grey[200]}`,
                  }}
                >
                  <Typography variant="body2">
                    No loans assigned to you
                  </Typography>
                </Box>
              ) : (
                <List
                  sx={{
                    py: 0,
                    maxHeight: 480,
                    overflowY: "auto",
                    border: `1px solid ${grey[100]}`,
                    borderRadius: 2,
                  }}
                >
                  {myLoans.map((loan) => (
                    <ListItem key={loan._id} disablePadding>
                      <ListItemButton
                        onClick={() =>
                          handleOnClick(loan.loanNumber, loan.loanName)
                        }
                        sx={{
                          py: 1,
                          px: 1,
                          borderRadius: 1,
                          borderBottom: `1px solid ${grey[100]}`,
                          "&:hover": {
                            backgroundColor: grey[50],
                          },
                        }}
                      >
                        <ListItemText
                          primary={
                            <Flx fw jb ac gap={1}>
                              <Flx ac gap={1}>
                                <Txt
                                  sx={{
                                    px: 0.8,
                                    py: 0.4,
                                    fontSize: "10px",
                                    backgroundColor: grey[200],
                                    borderRadius: "40px",
                                    flexShrink: 0,
                                  }}
                                >
                                  #{loan.loanNumber}
                                </Txt>
                                <Txt
                                  sx={{
                                    fontSize: "11px",
                                    flexShrink: 0,
                                  }}
                                >
                                  {loan.loanName}
                                </Txt>
                              </Flx>
                              <Flx ac gap={1}>
                                {loan.loanStatus && (
                                  <Txt
                                    sx={{
                                      fontSize: "10px",
                                      color: grey[500],
                                      ml: "auto",
                                      flexShrink: 0,
                                    }}
                                  >
                                    {loan.loanStatus}
                                  </Txt>
                                )}
                                <Tooltip title="Open in new tab">
                                  <Box
                                    sx={{
                                      borderRadius: "50%",
                                      ":hover": {
                                        background: blue[50],
                                      },
                                    }}
                                  >
                                    <OpenInNewTabButton
                                      size={15}
                                      to={`/loan/${loan.loanNumber}`}
                                    />
                                  </Box>
                                </Tooltip>
                              </Flx>
                            </Flx>
                          }
                          primaryTypographyProps={{
                            variant: "body2",
                            sx: { fontWeight: 400 },
                          }}
                        />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              )}
            </Box>
          )}
        </>
      )}
    </BasicModal>
  );
});

export default LoanSearchModal;
